import React, { useState, useEffect } from 'react';
import { Card, Select, Spin } from 'antd';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { plantApiGet } from '../../utils/plant_api';
import { defaultColors } from '@/themes/antd-theme';

const { Option } = Select;

const SensorDataChart = ({ sensor }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('1h');

  useEffect(() => {
    if (sensor) {
      fetchSensorData();
    }
  }, [sensor, timeRange]);

  const fetchSensorData = async () => {
    if (!sensor) return;

    setLoading(true);
    try {
      const response = await plantApiGet(`/api/sensors/${sensor.id}/data`, {
        time_range: timeRange
      });

      const formattedData = (response.data || []).map(item => ({
        timestamp: new Date(item.timestamp).toLocaleTimeString(),
        value: parseFloat(item.value_numeric),
        fullTime: new Date(item.timestamp).toLocaleString()
      }));

      setData(formattedData);
    } catch (err) {
      console.error('Error fetching sensor data:', err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Card className="p-3 shadow-lg">
          <p className="text-xs mb-1">
            {payload[0].payload.fullTime}
          </p>
          <p className="text-sm font-semibold mb-0" style={{ color: payload[0].color }}>
            {payload[0].value.toFixed(2)} {sensor?.sensor_type_details?.unit_of_measurement}
          </p>
        </Card>
      );
    }
    return null;
  };

  if (!sensor) {
    return (
      <Card className="p-6 text-center">
        <p className="text-sm text-gray-600 mb-0">
          Select a sensor to view data
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h6 className="text-base font-semibold m-0">
          {sensor.sensor_name} - Data Trend
        </h6>
        <Select
          value={timeRange}
          style={{ minWidth: 120 }}
          onChange={(value) => setTimeRange(value)}
        >
          <Option value="15m">Last 15 min</Option>
          <Option value="1h">Last 1 hour</Option>
          <Option value="4h">Last 4 hours</Option>
          <Option value="24h">Last 24 hours</Option>
          <Option value="7d">Last 7 days</Option>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Spin size="large" />
        </div>
      ) : data.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-sm text-gray-600 mb-0">
            No data available for selected time range
          </p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={data}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="timestamp"
              tick={{ fontSize: 12 }}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 12 }}
              label={{
                value: sensor.sensor_type_details?.unit_of_measurement || '',
                angle: -90,
                position: 'insideLeft'
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />

            {sensor.max_threshold && (
              <ReferenceLine
                y={parseFloat(sensor.max_threshold)}
                stroke={defaultColors.error}
                strokeDasharray="3 3"
                label={{ value: 'Max', position: 'right', fill: defaultColors.error }}
              />
            )}
            {sensor.min_threshold && (
              <ReferenceLine
                y={parseFloat(sensor.min_threshold)}
                stroke={defaultColors.warning}
                strokeDasharray="3 3"
                label={{ value: 'Min', position: 'right', fill: defaultColors.warning }}
              />
            )}

            <Line
              type="monotone"
              dataKey="value"
              stroke={defaultColors.primary}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              name={sensor.sensor_type_details?.name || 'Value'}
            />
          </LineChart>
        </ResponsiveContainer>
      )}

      <div className="mt-4 flex gap-4 justify-center">
        <div className="flex items-center gap-1">
          <div className="w-4 h-1" style={{ backgroundColor: defaultColors.error }} />
          <span className="text-xs text-gray-600">
            Max Threshold: {sensor.max_threshold || 'N/A'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-1" style={{ backgroundColor: defaultColors.warning }} />
          <span className="text-xs text-gray-600">
            Min Threshold: {sensor.min_threshold || 'N/A'}
          </span>
        </div>
      </div>
    </Card>
  );
};

export default SensorDataChart;
